import axiosInstance from '@api/axiosInstance';
import useDialog from '@hooks/useDialog';
import { AddRounded, CheckRounded, EditRounded } from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    Popover,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { hueFromHex, stringToColor } from '@utils/colorUtils';
import { hueToLabelColor } from '@utils/contactUtils';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// TODO: Improve Edit label UI and language

const EditLabel = ({ anchorEL, open, onClose, addLabel, label }) => {
    const { t } = useTranslation();
    const { contactId } = useParams({});
    const queryClient = useQueryClient();
    const deleteDialog = useDialog();

    const invalidateContact = () =>
        queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
    const invalidateLabels = () =>
        queryClient.invalidateQueries({
            queryKey: ['contactLabels', contactId],
        });

    const { register, control, getValues } = useForm({
        defaultValues: {
            name: label?.name || null,
            hue: label?.hue || Math.round(Math.random() * 360),
        },
    });

    const { mutate: createLabel } = useMutation({
        mutationFn: () => axiosInstance.post('contacts/labels', getValues()),
        onSuccess: () => {
            invalidateLabels();
            addLabel(getValues().name);
            invalidateContact();
            onClose();
        },
    });

    const { mutate: updateLabel } = useMutation({
        mutationFn: () =>
            axiosInstance.patch(`contacts/labels/${label.name}`, getValues()),
        onSuccess: () => {
            invalidateLabels();
            invalidateContact();
            onClose();
        },
    });

    const { mutate: deleteLabel } = useMutation({
        mutationFn: () => axiosInstance.delete(`contacts/labels/${label.name}`),
        onSuccess: () => {
            invalidateLabels();
            invalidateContact();
            deleteDialog.close();
            onClose();
        },
    });

    const deleteLabelDialog = () => {
        deleteDialog.open({
            title: t('Delete label'),
            onDelete: deleteLabel,
        });
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEL}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        p: 2,
                    },
                },
            }}
        >
            <Stack>
                <TextField
                    size="small"
                    fullWidth
                    {...register('name')}
                    placeholder={t('Enter label')}
                />
                <Controller
                    name="hue"
                    control={control}
                    render={({ field: { onChange, ...fieldProps } }) => (
                        <Slider
                            {...fieldProps}
                            onChange={(_, value) => onChange(value)}
                            max={360}
                            track={false}
                            sx={{
                                color: hueToLabelColor(fieldProps.value),
                                '& .MuiSlider-rail': {
                                    height: 12,
                                    opacity: 'unset',
                                    background: `-webkit-gradient(linear, left top, right top, from(${hueToLabelColor(
                                        0,
                                    )}), color-stop(17%, ${hueToLabelColor(
                                        60,
                                    )}), color-stop(33%, ${hueToLabelColor(
                                        120,
                                    )}), color-stop(50%, ${hueToLabelColor(
                                        180,
                                    )}), color-stop(66%, ${hueToLabelColor(
                                        240,
                                    )}), color-stop(83%, ${hueToLabelColor(
                                        300,
                                    )}), to(${hueToLabelColor(360)}))`,
                                },
                            }}
                        />
                    )}
                />
                <Stack direction="row" justifyContent="flex-end">
                    <Button onClick={label ? updateLabel : createLabel}>
                        {t('Save')}
                    </Button>
                    {label ? (
                        <Button onClick={deleteLabelDialog}>
                            {t('Delete')}
                        </Button>
                    ) : (
                        <Button onClick={onClose}>{t('Cancel')}</Button>
                    )}
                </Stack>
            </Stack>
        </Popover>
    );
};

const Label = ({ anchorEL, selected, label, addLabel, removeLabel }) => {
    const [editLabelOpen, setEditLabelOpen] = useState(false);

    return (
        <Stack direction="row" alignItems="center" width={240}>
            <Box
                sx={{
                    backgroundColor: hueToLabelColor(label.hue),
                    borderRadius: them => them.shape.xs,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="bodyLarge"
                    color="textSecondary"
                    sx={{ py: 0.5, px: 1, flex: 1 }}
                >
                    {label.name}
                </Typography>
                <IconButton
                    color="primary"
                    onClick={() => setEditLabelOpen(true)}
                >
                    <EditRounded />
                </IconButton>
            </Box>
            <IconButton
                color="primary"
                onClick={
                    selected
                        ? () => removeLabel(label.name)
                        : () => addLabel(label.name)
                }
            >
                {selected ? <CheckRounded /> : <AddRounded />}
            </IconButton>
            <EditLabel
                label={label}
                open={editLabelOpen}
                addLabel={addLabel}
                anchorEL={anchorEL}
                onClose={() => setEditLabelOpen(false)}
            />
        </Stack>
    );
};

const SelectLabel = ({ anchorEL, onClose, currentLabels }) => {
    const { t } = useTranslation();
    const { contactId } = useParams({});
    const queryClient = useQueryClient();
    const [createLabelOpen, setCreateLabelOpen] = useState(false);
    const [search, setSearch] = useState('');

    const invalidateContact = () =>
        queryClient.invalidateQueries({ queryKey: ['contact', contactId] });

    const { data: labels, isLoading } = useQuery({
        queryKey: ['contactLabels', contactId],
        queryFn: () =>
            axiosInstance.get('contacts/labels').then(res => res.data),
    });

    const filteredLabels =
        labels?.filter(label =>
            label.name.toLowerCase().includes(search.toLowerCase()),
        ) || [];

    const { mutate: addLabel } = useMutation({
        mutationFn: label =>
            axiosInstance.post('contacts/labels/add', {
                contact_ids: [contactId],
                label_name: label,
            }),
        onSuccess: invalidateContact,
    });

    const { mutate: removeLabel } = useMutation({
        mutationFn: label =>
            axiosInstance.post('contacts/labels/remove', {
                contact_ids: [contactId],
                label_name: label,
            }),
        onSuccess: invalidateContact,
    });

    // if (isLoading) return <h2>Loading...</h2>;

    return (
        <Popover
            open={Boolean(anchorEL)}
            anchorEl={anchorEL}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        p: 2,
                    },
                },
            }}
        >
            <Stack spacing={2}>
                <Typography variant="labelLarge">
                    {t('Select label')}
                </Typography>
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={t('Search labels') + '...'}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <Stack spacing={1} maxHeight={300} overflow="auto">
                    {filteredLabels.map(label => (
                        <Label
                            key={label.name}
                            label={label}
                            selected={currentLabels.some(
                                l => l.name === label.name,
                            )}
                            addLabel={addLabel}
                            removeLabel={removeLabel}
                            anchorEL={anchorEL}
                        />
                    ))}
                </Stack>
                <Button
                    variant="tonal"
                    onClick={() => setCreateLabelOpen(true)}
                >
                    {t('Create New Label')}
                </Button>
            </Stack>
            <EditLabel
                open={createLabelOpen}
                anchorEL={anchorEL}
                onClose={() => setCreateLabelOpen(false)}
                addLabel={addLabel}
            />
        </Popover>
    );
};

export default SelectLabel;
